// ExportModal — three-section export configuration modal (format, sections,
// options). Generate button calls useCreateExport and shows a self-dismissing
// toast; the modal stays open so the request can be watched through the
// shared RecentExportsList below the form, which lists every export except
// report exports (those have their own dialog on the Reports tab). Activity
// log is unchecked by default.

import { useState } from "react";
import Modal from "../../common/Modal";
import { useCreateExport } from "../../../hooks/useContractExports";
import RecentExportsList from "./RecentExportsList";
import type { ExportSections, ExportOptions } from "../../../types";

interface ExportModalProps {
  contractId: string;
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_SECTIONS: ExportSections = {
  details: true,
  insurances: true,
  deliverables: true,
  milestones: true,
  meetings: true,
  variationsSows: true,
  kpis: true,
  activityLog: false,
};

const DEFAULT_OPTIONS: ExportOptions = {
  includeArchived: false,
  includeEvidenceUrls: true,
};

const SECTION_LABELS: { key: keyof ExportSections; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "insurances", label: "Insurances" },
  { key: "deliverables", label: "Deliverables" },
  { key: "milestones", label: "Milestones" },
  { key: "meetings", label: "Meetings" },
  { key: "variationsSows", label: "Variations & SOWs" },
  { key: "kpis", label: "KPIs" },
  { key: "activityLog", label: "Activity log" },
];

function ToastBanner({ message }: { message: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-3 text-body-sm text-white shadow-lg dark:bg-slate-700"
    >
      {message}
    </div>
  );
}

export default function ExportModal({
  contractId,
  isOpen,
  onClose,
}: ExportModalProps) {
  const [formats, setFormats] = useState<{ pdf: boolean; xlsx: boolean }>({
    pdf: true,
    xlsx: true,
  });
  const [sections, setSections] = useState<ExportSections>(DEFAULT_SECTIONS);
  const [options, setOptions] = useState<ExportOptions>(DEFAULT_OPTIONS);
  const [toast, setToast] = useState<string | null>(null);

  const createExport = useCreateExport(contractId);

  const selectedFormatList = (
    Object.entries(formats) as ["pdf" | "xlsx", boolean][]
  )
    .filter(([, v]) => v)
    .map(([k]) => k);

  const allSectionsOn = SECTION_LABELS.every((s) => sections[s.key]);
  const atLeastOneSection = SECTION_LABELS.some((s) => sections[s.key]);
  const canGenerate = selectedFormatList.length > 0 && atLeastOneSection;

  const sectionCount = SECTION_LABELS.filter((s) => sections[s.key]).length;
  const formatCount = selectedFormatList.length;
  const estimateLine =
    formatCount > 0
      ? `Estimated: ${formatCount} ${formatCount === 1 ? "format" : "formats"} · ${sectionCount} ${sectionCount === 1 ? "section" : "sections"}`
      : "Select at least one format";

  function toggleSection(key: keyof ExportSections) {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleAllSections() {
    const next = !allSectionsOn;
    const updated = { ...sections };
    for (const s of SECTION_LABELS) updated[s.key] = next;
    setSections(updated);
  }

  async function handleGenerate() {
    await createExport.mutateAsync({
      formats: selectedFormatList,
      sections,
      options,
    });
    // Modal stays open so the user can watch the new request flow through
    // the Recent exports panel below (pending → ready) and download from
    // there once it's done — typically within ~20 seconds.
    setToast("Export queued · see status below");
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <>
      {toast && <ToastBanner message={toast} />}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Export contract"
        maxWidth="sm:max-w-lg"
      >
        <div className="space-y-6">
          {/* Format */}
          <section>
            <h4 className="text-body-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Format
            </h4>
            <div className="flex gap-3">
              {(
                [
                  { key: "pdf", label: "PDF" },
                  { key: "xlsx", label: "Excel" },
                ] as const
              ).map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 flex-1 cursor-pointer rounded-lg border p-3 transition-colors ${
                    formats[key]
                      ? "border-brand-400 bg-brand-50 dark:bg-brand-950/30 dark:border-brand-500"
                      : "border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    aria-label={label}
                    checked={formats[key]}
                    onChange={() =>
                      setFormats((prev) => ({ ...prev, [key]: !prev[key] }))
                    }
                    className="rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-400"
                  />
                  <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Sections */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-body-sm font-semibold text-slate-900 dark:text-slate-100">
                Sections
              </h4>
              <button
                type="button"
                onClick={toggleAllSections}
                className="text-caption text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                {allSectionsOn ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SECTION_LABELS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 cursor-pointer text-body-sm text-slate-700 dark:text-slate-300"
                >
                  <input
                    type="checkbox"
                    aria-label={label}
                    checked={sections[key]}
                    onChange={() => toggleSection(key)}
                    className="rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-400"
                  />
                  {label}
                </label>
              ))}
            </div>
          </section>

          {/* Options */}
          <section>
            <h4 className="text-body-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Options
            </h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-body-sm text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  aria-label="Include archived items"
                  checked={options.includeArchived}
                  onChange={() =>
                    setOptions((prev) => ({
                      ...prev,
                      includeArchived: !prev.includeArchived,
                    }))
                  }
                  className="rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-400"
                />
                Include archived items
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-body-sm text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  aria-label="Include evidence URLs"
                  checked={options.includeEvidenceUrls}
                  onChange={() =>
                    setOptions((prev) => ({
                      ...prev,
                      includeEvidenceUrls: !prev.includeEvidenceUrls,
                    }))
                  }
                  className="rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-400"
                />
                Include evidence URLs
              </label>
            </div>
          </section>

          {/* Estimate + actions */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
            <p className="text-caption text-slate-500 dark:text-slate-400">
              {estimateLine}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary py-2 px-4"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate || createExport.isPending}
                className="btn-primary py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createExport.isPending ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>

          {/* Recent exports — populated only while the modal is open. */}
          <RecentExportsList contractId={contractId} isOpen={isOpen} kind="contract" />
        </div>
      </Modal>
    </>
  );
}
